// **1ステージぶんの成績レコード**。ポータル横断（どのゲームも同じ形で持つ）。
//
// この形は D1 `player_stage_progress.parts` に**そのまま JSON で入る**（サーバーは中身を
// 解釈しない）。ゲームごとに別の形にすると、後から集計するときに読み手が分岐だらけになるので、
// **記録の粒度と意味だけは全ゲームで揃える**。何をもって1レコードとするかはゲームが決めてよい:
//   - 二角取り: 1ステージ = 18パート（6サイズ×3難易度）の1セル
//   - Triangle Sweeper: 13形状 × 3難易度 の1マス
//
// ※ 2026-08-02 に `games/nikakudori/game/progress.ts` から切り出した。中身はゲーム固有の
//   要素が無く、2本目も同じ意味で使うため。ゲーム間の直接 import は禁止なので、
//   共有するなら platform に置くのが唯一の形（rng.ts と同じ理由）。

/** 1プレイで取れるスターの上限。**★は全ゲーム共通の通貨**なので 0..3 で揃える。 */
export const MAX_RECORD_STARS = 3;

/** 1マスぶんの記録（原作 二角取り `CNikaStagePartModel` 相当）。 */
export interface StageRecord {
  /** 獲得スター数のベスト 0..3。 */
  stars: number;
  /** 最高スコア。 */
  best: number;
  /** 最短クリア時間(ms)。0 = 未クリア。 */
  fastestMs: number;
  /** クリア回数。0 = 未クリア。 */
  clears: number;
  /** プレイ回数（ゲーム開始のたびに +1）。 */
  games: number;
  /** クリア時スコアの合計。平均スコア＝scoreSum/clears。 */
  scoreSum: number;
  /** 通算プレイ時間(ms)。クリアしなくても積む。 */
  playMs: number;
}

export function emptyRecord(): StageRecord {
  return { stars: 0, best: 0, fastestMs: 0, clears: 0, games: 0, scoreSum: 0, playMs: 0 };
}

const nonNegInt = (v: unknown): number =>
  typeof v === 'number' && Number.isFinite(v) ? Math.max(0, Math.trunc(v)) : 0;

/**
 * 保存済み（localStorage / サーバ）の1レコードを現行の形へ揃える。
 * `games` / `scoreSum` / `playMs` は後から追加した項目なので、持っていない旧データは
 * 「クリアしたぶんだけ遊んだ」「毎回ベストスコアを出した」とみなして埋める
 * （＝平均スコア＝最高スコア・★獲得率は最大値になる。古い記録を過小評価しない）。
 * プレイ時間だけは推測しようがないので 0 のまま（記録し始めた時点から積み上がる）。
 */
export function normalizeRecord(v: unknown, maxStars = MAX_RECORD_STARS): StageRecord {
  const o = (typeof v === 'object' && v !== null ? v : {}) as Partial<StageRecord>;
  const clears = nonNegInt(o.clears);
  const best = nonNegInt(o.best);
  return {
    stars: Math.min(maxStars, nonNegInt(o.stars)),
    best,
    fastestMs: nonNegInt(o.fastestMs),
    clears,
    // プレイ回数はクリア回数を下回らない（率が 100% を超えないように）。
    games: Math.max(clears, o.games === undefined ? clears : nonNegInt(o.games)),
    scoreSum: o.scoreSum === undefined ? best * clears : nonNegInt(o.scoreSum),
    playMs: nonNegInt(o.playMs),
  };
}

/** ゲーム開始を1レコードへ反映（非破壊）。 */
export function applyStart(p: StageRecord): StageRecord {
  return { ...p, games: p.games + 1 };
}

/**
 * プレイ時間を1レコードへ積む（非破壊）。
 * **クリアしなくても積む**（中断・ゲームオーバーでも遊んだ時間には違いない）。
 */
export function applyPlayTime(p: StageRecord, ms: number): StageRecord {
  return ms <= 0 ? p : { ...p, playMs: p.playMs + Math.round(ms) };
}

/**
 * クリア結果を1レコードへ反映した新レコードを返す（非破壊）。
 * - clears++、best は高い方、stars は多い方（ベスト保持）、fastestMs は短い方（初回は採用）。
 * - scoreSum はクリア時スコアを積む（平均スコア用）。games は開始時に数えているが、
 *   開始を記録できていない経路のぶんもクリア回数を下回らないよう底上げする。
 *
 * ⚠️ **`timeMs` は丸めてから入れる**（`applyPlayTime` と同じ）。呼び出し側の経過時間は
 *    Phaser の delta 由来で小数を持つ（実測 `81738.2`）。`normalizeRecord` とサーバーの
 *    `sanitizePart` がどのみち整数へ切るので、丸めないと**保存した値と読み直した値が食い違う**。
 */
export function applyClear(
  p: StageRecord,
  score: number,
  stars: number,
  timeMs: number,
): StageRecord {
  const t = Math.max(0, Math.round(timeMs));
  return {
    clears: p.clears + 1,
    best: Math.max(p.best, score),
    stars: Math.max(p.stars, stars),
    fastestMs: p.fastestMs === 0 ? t : Math.min(p.fastestMs, t),
    games: Math.max(p.games, p.clears + 1),
    scoreSum: p.scoreSum + Math.max(0, score),
    playMs: p.playMs,
  };
}

/** 最速タイム(ms) を "Nm Ss" 形式へ。未クリア(0)は "-:-"。 */
export function formatFastest(ms: number): string {
  if (ms <= 0) return '-:-';
  const total = Math.floor(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}m ${s}s`;
}
